import { TriangleAlert } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { useConfirmationWizard } from '@/features/Quote/hooks/useConfirmationWizard';
import { usePanelOpenClose } from '@/features/Quote/hooks/usePanelOpenClose';
import { formatToOneDecimal, getCurrentAbsenceBaseYear } from '@/services/utils';
import type { AbsenceValues, SettingType } from '@/types/app';
import Alert from '../Alert';
import DetailCard from '../DetailCard';
import StepConfirmButton from '../StepConfirmButton';
import AbsenceInput from './Input';
import { type AbsenceInfo, getSectorAverageYears, toAbsenceValues } from './values';

const SECTOR_AVERAGE_HINT_ID = 'absence-sector-average-hint';

const formatAbsenceInputValue = (value: string): string => {
	const normalizedValue = value.replace(',', '.');
	const numValue = Math.min(Math.max(Number.parseFloat(normalizedValue || '0'), 0), 99.9);
	return formatToOneDecimal(numValue);
};

interface DetailsProps {
	activeSetting: SettingType | null;
	setActiveSetting: (setting: SettingType | null) => void;
	openedFromQuoteSelection?: boolean;
	confirmedSteps: ReadonlySet<SettingType>;
	markStepConfirmed: (step: SettingType) => void;
}

const Details = ({
	activeSetting,
	setActiveSetting,
	openedFromQuoteSelection = false,
	confirmedSteps,
	markStepConfirmed
}: DetailsProps) => {
	const { t } = useTranslation();
	const { appData, isSubmitting, premiumVarManagement } = useAppContext();
	const { getNextUnconfirmedStep } = useConfirmationWizard();
	// Next warning the user still has to confirm this session, or null when absence is
	// the terminal step and confirming should recalculate everything at once.
	const nextStep = getNextUnconfirmedStep('absence', confirmedSteps);

	// Absence-related state
	const [absenceValues, setAbsenceValues] = useState<AbsenceValues>({
		vz1: '',
		vz2: '',
		vz3: ''
	});

	// Absence-related handlers
	const absenceInfo = useMemo(
		() => appData?.premium_variables?.absence_info || null,
		[appData?.premium_variables?.absence_info]
	);

	// Years in on-screen (vz1..vz3) order; the ones still on the sector average get flagged.
	const years = useMemo(() => {
		const baseYear = getCurrentAbsenceBaseYear();
		return [baseYear - 1, baseYear - 2, baseYear - 3].map(String);
	}, []);
	const sectorAverageYears = useMemo(
		() => getSectorAverageYears(absenceInfo as AbsenceInfo),
		[absenceInfo]
	);
	const flaggedCount = years.filter((year) => sectorAverageYears.has(year)).length;

	const handleAbsenceInfoChange = useCallback((absenceInfo: AbsenceInfo) => {
		setAbsenceValues(toAbsenceValues(absenceInfo));
	}, []);

	const handleAbsenceFieldChange = useCallback(
		(field: keyof AbsenceValues, originalValue: string) => {
			let value = originalValue;
			// Limit length to 4 characters (excluding decimal separator - comma or period)
			if (value.toString().replace(/[.,]/g, '').length > 4) {
				value = value.slice(0, 4);
			}

			// Normalize comma to period for numeric comparison
			const normalizedValue = value.replace(',', '.');
			// Ensure value is not above 99.9
			const numValue = Number.parseFloat(normalizedValue || '0');
			if (numValue > 99.9) return;

			// Use functional setState to avoid stale closure issues
			setAbsenceValues((prev) => ({
				...prev,
				[field]: value
			}));
		},
		[]
	);

	const handleAbsenceFieldBlur = useCallback(
		(field: keyof AbsenceValues, value: string) => {
			const formattedValue = formatAbsenceInputValue(value);

			setAbsenceValues((prev) => {
				const newValues = { ...prev, [field]: formattedValue };
				// Live-update the summary card for real-time feedback. Unconfirmed edits are
				// restored when the panel is cancelled (see the open/close effect) and are
				// never persisted unless the user confirms via confirmStep.
				premiumVarManagement.absence.update(newValues);
				return newValues;
			});
		},
		[premiumVarManagement.absence]
	);

	const handleAbsenceFieldInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		// Exclude decimal separators (comma or period) from length calculation
		if (target.value.toString().replace(/[.,]/g, '').length > 4) {
			target.value = target.value.slice(0, 4);
		}
	}, []);

	// Tracks whether the panel was left via confirm (vs cancelled), so the cancel
	// handler knows whether to keep the live edits or restore the committed values.
	const confirmedRef = useRef(false);
	const originalAbsenceRef = useRef<AbsenceValues | null>(null);
	const vz1InputRef = useRef<HTMLInputElement | null>(null);
	const vz2InputRef = useRef<HTMLInputElement | null>(null);
	const vz3InputRef = useRef<HTMLInputElement | null>(null);

	const getFormattedAbsenceValues = useCallback((): AbsenceValues => {
		const formatFromRef = (
			ref: React.RefObject<HTMLInputElement | null>,
			fallback: string | undefined
		): string => formatAbsenceInputValue(ref.current?.value ?? fallback ?? '');

		return {
			vz1: formatFromRef(vz1InputRef, absenceValues.vz1),
			vz2: formatFromRef(vz2InputRef, absenceValues.vz2),
			vz3: formatFromRef(vz3InputRef, absenceValues.vz3)
		};
	}, [absenceValues]);

	// Confirm absence (optimistic green) and move on. Values are pushed to pending so
	// the single deferred recalculation in Settings can commit them. `target` is the
	// next unconfirmed step, or null when this was the last card (closing the panel
	// triggers the commit).
	const confirmStep = useCallback(
		(target: SettingType | null) => {
			const formattedValues = getFormattedAbsenceValues();
			setAbsenceValues(formattedValues);
			confirmedRef.current = true;
			premiumVarManagement.absence.update(formattedValues);
			premiumVarManagement.absence.updatePendingValues(formattedValues);
			markStepConfirmed('absence');
			setActiveSetting(target);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
		[getFormattedAbsenceValues, premiumVarManagement.absence, setActiveSetting, markStepConfirmed]
	);

	const focusAbsenceField = useCallback((ref: React.RefObject<HTMLInputElement | null>) => {
		ref.current?.focus();
		ref.current?.select();
	}, []);

	const blurAndFocusNext = useCallback(
		(
			currentRef: React.RefObject<HTMLInputElement | null>,
			nextRef: React.RefObject<HTMLInputElement | null>
		) => {
			currentRef.current?.blur();
			focusAbsenceField(nextRef);
		},
		[focusAbsenceField]
	);

	const handleVz3Enter = useCallback(() => {
		if (isSubmitting) return;
		confirmStep(nextStep);
	}, [confirmStep, isSubmitting, nextStep]);

	// Initialize absence values when absenceInfo changes
	useEffect(() => {
		handleAbsenceInfoChange(absenceInfo as AbsenceInfo);
	}, [absenceInfo, handleAbsenceInfoChange]);

	// Snapshot the committed values on open and, if the panel closes without a confirm
	// (cancel / click-outside), restore them so the live blur edits don't leak into
	// shared state. Mirrors the coverage and employees panels.
	const handleOpen = useCallback(() => {
		confirmedRef.current = false;
		originalAbsenceRef.current = toAbsenceValues(absenceInfo as AbsenceInfo);
	}, [absenceInfo]);

	const handleClose = useCallback(() => {
		if (!confirmedRef.current && originalAbsenceRef.current) {
			premiumVarManagement.absence.update(originalAbsenceRef.current);
		}
		confirmedRef.current = false;
	}, [premiumVarManagement.absence]);

	usePanelOpenClose(activeSetting === 'absence', { onOpen: handleOpen, onClose: handleClose });

	return (
		<DetailCard
			title={t('quotes.settings.absence.panelTitle')}
			dataAttribute="edit-absence-card"
			type="absence"
			isVisible={activeSetting === 'absence'}
			onClose={() => setActiveSetting(null)}
		>
			{openedFromQuoteSelection && (
				<Alert
					variant="info"
					className="my-4"
					testId="absence-info-alert"
					showIcon
					text={t('quotes.settings.absence.updateWarning')}
				/>
			)}
			<h6 className="font-bold text-lg mb-2">{t('quotes.settings.absence.title')}</h6>
			<div className="font-medium text-muted-foreground mb-2 leading-relaxed">
				{t('quotes.settings.absence.detailDescription')}
				<br />
				{t('quotes.settings.absence.detailSubDescription')}
			</div>
			<div className="flex flex-col gap-3 pt-3">
				<div className="flex gap-2 max-w-[400px]">
					<AbsenceInput
						key="vz1Input"
						index="1"
						absenceValues={absenceValues}
						handleAbsenceFieldChange={handleAbsenceFieldChange}
						handleAbsenceFieldBlur={handleAbsenceFieldBlur}
						handleAbsenceFieldInput={handleAbsenceFieldInput}
						inputRef={vz1InputRef}
						sectorAverageHintId={
							sectorAverageYears.has(years[0]) ? SECTOR_AVERAGE_HINT_ID : undefined
						}
						onEnterKey={() => blurAndFocusNext(vz1InputRef, vz2InputRef)}
					/>
					<AbsenceInput
						key="vz2Input"
						index="2"
						absenceValues={absenceValues}
						handleAbsenceFieldChange={handleAbsenceFieldChange}
						handleAbsenceFieldBlur={handleAbsenceFieldBlur}
						handleAbsenceFieldInput={handleAbsenceFieldInput}
						inputRef={vz2InputRef}
						sectorAverageHintId={
							sectorAverageYears.has(years[1]) ? SECTOR_AVERAGE_HINT_ID : undefined
						}
						onEnterKey={() => blurAndFocusNext(vz2InputRef, vz3InputRef)}
					/>
					<AbsenceInput
						key="vz3Input"
						index="3"
						absenceValues={absenceValues}
						handleAbsenceFieldChange={handleAbsenceFieldChange}
						handleAbsenceFieldBlur={handleAbsenceFieldBlur}
						handleAbsenceFieldInput={handleAbsenceFieldInput}
						inputRef={vz3InputRef}
						sectorAverageHintId={
							sectorAverageYears.has(years[2]) ? SECTOR_AVERAGE_HINT_ID : undefined
						}
						onEnterKey={handleVz3Enter}
					/>
				</div>
				{flaggedCount > 0 && (
					<div
						id={SECTOR_AVERAGE_HINT_ID}
						className="flex items-start gap-1.5 px-1 py-4 text-xs text-muted-foreground"
						data-testid="absence-sector-average-feedback"
					>
						<TriangleAlert className="size-3.5 shrink-0 text-yellow-500" aria-hidden />
						<span>{t('quotes.settings.absence.sectorAverageHint', { count: flaggedCount })}</span>
					</div>
				)}
				<StepConfirmButton
					nextStep={nextStep}
					onConfirm={confirmStep}
					disabled={isSubmitting}
					loading={isSubmitting}
					confirmAsNextLabel
				/>
			</div>
		</DetailCard>
	);
};
export default Details;
